import { useCallback, useEffect, useState } from 'react';

import {
  alpha,
  Box,
  CircularProgress,
  IconButton,
  List,
  ListItemText,
  makeStyles,
  Slide,
  Typography,
} from '@material-ui/core';
import {
  Close,
  DoneAllOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@material-ui/icons';
import { format, isToday, parseISO } from 'date-fns';
import { useDebouncedCallback } from 'use-debounce';

import { chatAPI } from 'services/api';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchMensagensByAtendimento,
  fetchMensagensByGrupo,
  fetchPreviewMensagensGrupo,
  mensagensSelector,
  navegarParaMensagem,
  navegarParaMensagemGrupo,
  resetMensagensGrupos,
  resetMensagensAtendimento,
  setIdMensagemPesquisada,
  PreviewMensagemGrupoInterface,
  PreviewMensagemInterface,
  setSearchQuery,
  resetSearchQuery,
  resetIdMensagemPesquisada,
  setCursorAConsumir,
} from 'store/reducers/mensagens';
import parseTextToHtml from 'utils/parseTextToHtml';

import SearchBar from './SearchBar';

const useStyles = makeStyles(({ spacing, palette, shape }) => ({
  root: {
    height: '100%',
    width: '100%',
    maxWidth: '100%',
    backgroundColor: palette.background.paper,
    borderTopRightRadius: shape.borderRadius,
    borderBottomRightRadius: shape.borderRadius,
    borderRight: `1px solid ${alpha(palette.divider, 0.07)}`,
    gap: spacing(2),
    display: 'flex',
    flexDirection: 'column',
  },
  adornment: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(0.5),
  },
  count: {
    fontSize: 12,
    color: palette.text.secondary,
    minWidth: spacing(4),
    textAlign: 'center',
    userSelect: 'none',
  },
  divider: {
    height: 20,
    margin: spacing(0, 0.5),
  },
  iconButton: {
    padding: 4,
  },

  list: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  listItem: {
    width: '100%',
    cursor: 'pointer',
    paddingBlock: spacing(1.5),
    paddingInline: spacing(3),
    borderTop: '1px solid #e0e0e0',
    margin: 0,

    '&:hover': {
      backgroundColor: palette.action.hover,
    },
  },

  previewText: {
    fontSize: 13,
    fontWeight: 400,
    color: palette.text.primary,
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitLineClamp: 2,
  },
}));

interface GenericAtendimentoForSearch {
  id: string;
  contato?: { id: string; nome: string };
  contact?: { id: string; name: string };
  canal?: { codigo?: string };
  channel?: { code?: string; id?: string; name?: string };
  canalCodigo?: string;
  canalInfo?: { canalCodigo?: string; canalId?: string };
}

interface GenericGrupoForSearch {
  id: string;
  nome: string;
}

interface PesquisarMensagensContainerProps {
  pesquisarMensagens: boolean;
  atendimento?: GenericAtendimentoForSearch;
  grupo?: GenericGrupoForSearch;
  setPesquisarMensagens: (value: boolean) => void;
  onSelectMessage?: (messageId: string, afterCursor: string) => void;
  onSearchChange?: (value: string) => void;
  direction?: 'left' | 'right' | 'up' | 'down';
}

export default function PesquisarMensagensContainer({
  atendimento,
  grupo,
  pesquisarMensagens,
  setPesquisarMensagens,
  onSelectMessage,
  onSearchChange,
  direction = 'right',
}: PesquisarMensagensContainerProps) {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [search, setSearch] = useState('');
  const grupoId = grupo?.id;

  const { mensagemEstaMontada } = useAppSelector(mensagensSelector);
  const [previewMensagens, setPreviewMensagens] = useState<
    (PreviewMensagemInterface | PreviewMensagemGrupoInterface)[]
  >([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [loadingPreviewMensagens, setLoadingPreviewMensagens] = useState(false);

  const clearPreviewMensagens = useCallback(() => {
    setPreviewMensagens([]);
    setCurrentIndex(-1);
  }, []);

  const getPreviewMensagens = useCallback(
    async (searchTerm: string) => {
      try {
        setLoadingPreviewMensagens(true);
        let data: (PreviewMensagemInterface | PreviewMensagemGrupoInterface)[];

        if (grupoId) {
          data = await dispatch(
            fetchPreviewMensagensGrupo({
              search: searchTerm,
              groupId: grupoId,
            }),
          ).unwrap();
        } else {
          const contactId =
            atendimento?.contato?.id || atendimento?.contact?.id;
          const channelCode =
            atendimento?.canal?.codigo ||
            atendimento?.channel?.code ||
            atendimento?.canalCodigo ||
            atendimento?.canalInfo?.canalCodigo;

          if (!channelCode) {
            setPreviewMensagens([]);
            setLoadingPreviewMensagens(false);
            return;
          }

          const response = await chatAPI.get('/v2/messages/preview', {
            params: { search: searchTerm, contactId, channelCode },
          });

          data = response.data as PreviewMensagemInterface[];
        }

        setPreviewMensagens(data);

        if (searchTerm) {
          if (data.length === 0) {
            dispatch(resetSearchQuery());
            if (onSearchChange) onSearchChange('');
          } else {
            dispatch(setSearchQuery(searchTerm));
            if (onSearchChange) onSearchChange(searchTerm);
          }
        } else {
          dispatch(resetSearchQuery());
          if (onSearchChange) onSearchChange('');
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        setLoadingPreviewMensagens(false);
      }
    },
    [
      atendimento?.canal?.codigo,
      atendimento?.channel?.code,
      atendimento?.contact?.id,
      atendimento?.contato?.id,
      atendimento?.canalCodigo,
      atendimento?.canalInfo?.canalCodigo,
      grupoId,
      dispatch,
      onSearchChange,
    ],
  );

  const debouncedSearch = useDebouncedCallback((value: string) => {
    getPreviewMensagens(value);
  }, 1000);

  const handleSearch = (value: string) => {
    setSearch(value);
    clearPreviewMensagens();
    if (value) {
      setLoadingPreviewMensagens(true);
    }
    debouncedSearch(value);
  };

  const handleCancelSearch = () => {
    setSearch('');
    clearPreviewMensagens();
    setLoadingPreviewMensagens(false);
    setCurrentIndex(-1);
    dispatch(resetSearchQuery());
    if (onSearchChange) onSearchChange('');
  };

  const [mensagemParaNavegar, setMensagemParaNavegar] = useState<{
    id: string;
    afterCursor: string;
  } | null>(null);

  const navigateTo = (index: number) => {
    if (index >= 0 && index < previewMensagens.length) {
      setCurrentIndex(index);
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      handleClickMensagem(previewMensagens[index].id);
    }
  };

  const handleNext = () => navigateTo(currentIndex + 1);
  const handlePrevious = () => navigateTo(currentIndex - 1);

  const handleClickMensagem = (id: string) => {
    const index = previewMensagens.findIndex(m => m.id === id);
    if (index !== -1) {
      setCurrentIndex(index);
    }

    const navegarPromise = grupoId
      ? dispatch(navegarParaMensagemGrupo(id)).unwrap()
      : dispatch(navegarParaMensagem(id)).unwrap();

    navegarPromise.then(data => {
      if (!atendimento && !grupoId) return;

      dispatch(resetIdMensagemPesquisada());
      dispatch(setIdMensagemPesquisada(id));

      if (onSelectMessage) {
        onSelectMessage(id, data.data.afterCursor);
      } else {
        setMensagemParaNavegar({
          id,
          afterCursor: data.data.afterCursor,
        });
      }
    });
  };

  useEffect(() => {
    return () => {
      dispatch(resetIdMensagemPesquisada());
      dispatch(resetSearchQuery());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!mensagemParaNavegar || (!atendimento && !grupoId)) return;

    const { afterCursor } = mensagemParaNavegar;

    if (!mensagemEstaMontada) {
      if (grupoId) {
        dispatch(resetMensagensGrupos());
        dispatch(
          fetchMensagensByGrupo({
            grupoChatId: grupoId,
            cursor: { afterCursor },
          }),
        )
          .unwrap()
          .then(data => {
            dispatch(setCursorAConsumir({ ...data.cursor, type: 'grupo' }));
          });
      } else if (atendimento) {
        dispatch(resetMensagensAtendimento());
        dispatch(
          fetchMensagensByAtendimento({
            atendimentoId: atendimento.id,
            cursor: { afterCursor },
          }),
        )
          .unwrap()
          .then(data => {
            const { cursor } = data;

            dispatch(setCursorAConsumir(cursor));
          });
      }
    }

    setMensagemParaNavegar(null);
  }, [
    mensagemEstaMontada,
    mensagemParaNavegar,
    dispatch,
    atendimento,
    grupoId,
  ]);

  const handleClose = () => {
    setSearch('');
    clearPreviewMensagens();
    setLoadingPreviewMensagens(false);
    debouncedSearch.cancel();
    dispatch(resetSearchQuery());
    if (onSearchChange) onSearchChange('');
    setPesquisarMensagens(false);
  };

  return (
    <Slide
      direction={direction}
      in={pesquisarMensagens}
      mountOnEnter
      unmountOnExit
    >
      <Box className={classes.root}>
        <Box
          display="flex"
          width="100%"
          alignItems="center"
          justifyContent="space-between"
          paddingX={2}
          paddingTop={2}
          gridGap={2}
        >
          <Typography variant="body1">Pesquisar mensagens</Typography>
          <IconButton size="small" onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>

        <Box paddingX={2}>
          <SearchBar
            onChange={handleSearch}
            onCancelSearch={handleCancelSearch}
            placeholder="Pesquisar mensagem"
            showSearchIcon
            value={search}
            endAdornment={
              previewMensagens.length > 0 && (
                <Box className={classes.adornment}>
                  <Typography className={classes.count}>
                    {currentIndex + 1}/{previewMensagens.length}
                  </Typography>
                  <IconButton
                    size="small"
                    className={classes.iconButton}
                    onClick={handleNext}
                    disabled={currentIndex >= previewMensagens.length - 1}
                  >
                    <KeyboardArrowUp fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    className={classes.iconButton}
                    onClick={handlePrevious}
                    disabled={currentIndex <= 0}
                  >
                    <KeyboardArrowDown fontSize="small" />
                  </IconButton>
                </Box>
              )
            }
          />
        </Box>

        <List className={classes.list}>
          {loadingPreviewMensagens && (
            <Box
              width="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="50vh"
            >
              <CircularProgress color="inherit" size={24} />
            </Box>
          )}

          {!loadingPreviewMensagens &&
            !search &&
            previewMensagens.length === 0 && (
              <Box
                width="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="50vh"
              >
                <Typography
                  variant="caption"
                  style={{
                    fontWeight: 500,
                    paddingInline: 16,
                    textAlign: 'center',
                  }}
                >
                  Pesquisar mensagens com{' '}
                  {grupo?.nome ||
                    atendimento?.contato?.nome ||
                    atendimento?.contact?.name}
                </Typography>
              </Box>
            )}

          {!loadingPreviewMensagens &&
            search &&
            previewMensagens.length === 0 && (
              <Box
                width="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="50vh"
              >
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ padding: 16 }}
                >
                  Nenhuma mensagem encontrada
                </Typography>
              </Box>
            )}

          {!loadingPreviewMensagens &&
            previewMensagens?.map(mensagem => {
              const date = parseISO(mensagem.registeredIn);
              const displayDate = isToday(date)
                ? format(date, 'HH:mm')
                : format(date, 'dd/MM/yyyy');

              return (
                <ListItemText
                  key={mensagem.id}
                  className={classes.listItem}
                  onClick={() => handleClickMensagem(mensagem.id)}
                  primaryTypographyProps={{ component: 'div' }}
                  secondaryTypographyProps={{ component: 'div' }}
                  primary={
                    <Typography variant="caption" color="textSecondary">
                      {displayDate}
                    </Typography>
                  }
                  secondary={
                    <Box display="flex" alignItems="center" gridGap={8}>
                      {mensagem.autor !== 'CONTATO' && (
                        <DoneAllOutlined fontSize="small" color="disabled" />
                      )}
                      <Typography
                        variant="caption"
                        className={classes.previewText}
                        dangerouslySetInnerHTML={{
                          __html: parseTextToHtml(
                            mensagem?.preview || '',
                            search,
                          ),
                        }}
                      />
                    </Box>
                  }
                />
              );
            })}
        </List>
      </Box>
    </Slide>
  );
}
